import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs, { type Dayjs } from 'dayjs';
import { Body2 } from '@/components/@IntranetLibrary/Typography';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import DatePicker from '@/components/@IntranetLibrary/DatePicker';
import Select from '@/components/@IntranetLibrary/Select';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import {
  buildSapBondStatusContentHtml,
  SapBondStatusSnapshot,
} from '@/components/Approval/@components/SapBondStatus';
import { getSapBondStatusDraftSourceUrl } from '@/constant/approval';
import useMyBrandOptions from '@/hooks/approval/useMyBrandOptions';
import { useSnackbar } from '@/hooks/global-tools';
import type {
  SapBondStatusData,
  SapBondStatusDraftSource,
} from '@/types/approval';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  formatSapBondStatusPeriod,
  SAP_BOND_STATUS_DEFAULT_BRAND,
  sapBondStatusCondition,
  type SapBondStatusState,
} from '@/utils/approval/sapBondStatus';
import { FieldBlock, FieldHeader } from '../styles';
import type { ApprovalFormValues } from '../types';

const CONTROL_HEIGHT = 44;

type SapBondStatusFieldProps = {
  /** 편집 진입 복원값 — 있으면 자동 조회 없이 저장된 조건·표를 그대로 보여 줌 */
  initialState: SapBondStatusState | null;
  isEditMode: boolean;
};

/**
 * SAP 채권현황(185) — 진입 시 기본 조건(MLB·이번 달)으로 1회 자동 조회, 이후 「확인」으로 재조회.
 * 조회 중엔 「확인」 잠금. 조회 실패여도 상신은 막지 않음(레거시 동일)
 */
const SapBondStatusField = ({
  initialState,
  isEditMode,
}: SapBondStatusFieldProps) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { setValue, getValues, watch } = useFormContext<ApprovalFormValues>();
  const bondStatus = watch('sapBondStatus');
  const { options: brandOptions, error: brandError } = useMyBrandOptions();

  const [brandCode, setBrandCode] = useState('');
  const [period, setPeriod] = useState<Dayjs | null>(() => dayjs());
  const isLoading = !!bondStatus?.loading;
  const requestIdRef = useRef(0);

  useEffect(
    () => () => {
      requestIdRef.current += 1;
    },
    [],
  );

  const brandLabel = useCallback(
    (code: string) => brandOptions.find((o) => o.value === code)?.label ?? code,
    [brandOptions],
  );

  const fetchBondStatus = useCallback(
    async (year: number, month: number, code: string, userQueried: boolean) => {
      const requestId = ++requestIdRef.current;
      const previous = getValues('sapBondStatus');
      setValue('sapBondStatus', {
        year,
        month,
        brandCode: code,
        brandName: brandLabel(code),
        data: null,
        loading: true,
        userQueried,
        entryCondition: userQueried
          ? previous?.entryCondition ??
            (previous ? sapBondStatusCondition(previous) : undefined)
          : sapBondStatusCondition({ brandCode: code, year, month }),
        entryHasData: userQueried
          ? previous?.entryHasData ?? !!previous?.data
          : false,
      });
      const finish = (data: SapBondStatusData | null) => {
        const current = getValues('sapBondStatus');
        if (current) {
          setValue('sapBondStatus', {
            ...current,
            data,
            loading: false,
            ...(userQueried ? {} : { entryHasData: !!data }),
          });
        }
      };
      try {
        const res = await getNestedDataFetcher<SapBondStatusDraftSource>(
          getSapBondStatusDraftSourceUrl({ year, month, brandCode: code }),
        );
        if (requestId !== requestIdRef.current) return;
        finish(res.display?.source ?? res.attributes?.data ?? null);
      } catch {
        if (requestId !== requestIdRef.current) return;
        finish(null);
        snackbar.showError({
          message: t('데이터를 불러오는데 실패했습니다. 다시 시도해 주세요.'),
        });
      }
    },
    [brandLabel, getValues, setValue, snackbar, t],
  );

  // 신규 작성은 브랜드 목록이 오면 1회 자동 조회, 편집은 저장 조건 복원
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    if (isEditMode) {
      if (!initialState) return;
      initializedRef.current = true;
      setBrandCode(initialState.brandCode);
      setPeriod(dayjs(new Date(initialState.year, initialState.month - 1, 1)));
      return;
    }
    if (!brandOptions.length && !brandError) return;
    initializedRef.current = true;
    const code = brandOptions.some(
      (o) => o.value === SAP_BOND_STATUS_DEFAULT_BRAND,
    )
      ? SAP_BOND_STATUS_DEFAULT_BRAND
      : brandOptions[0]?.value ?? SAP_BOND_STATUS_DEFAULT_BRAND;
    const now = dayjs();
    setBrandCode(code);
    setPeriod(now);
    fetchBondStatus(now.year(), now.month() + 1, code, false);
  }, [isEditMode, initialState, brandOptions, brandError, fetchBondStatus]);

  const brandErrorShownRef = useRef(false);
  useEffect(() => {
    if (!brandError || brandErrorShownRef.current) return;
    brandErrorShownRef.current = true;
    snackbar.showError({ message: t('브랜드 리스트를 불러올 수 없습니다.') });
  }, [brandError, snackbar, t]);

  // 편집 복원값은 브랜드명이 없어 목록 도착 후 채움 — 저장 표의 브랜드 칸에 쓰임
  useEffect(() => {
    if (!bondStatus) return;
    const label = brandOptions.find(
      (o) => o.value === bondStatus.brandCode,
    )?.label;
    if (!label || label === bondStatus.brandName) return;
    setValue(
      'sapBondStatus',
      { ...bondStatus, brandName: label },
      { shouldDirty: false },
    );
  }, [bondStatus, brandOptions, setValue]);

  const handleConfirm = () => {
    if (isLoading || !brandCode || !period?.isValid()) return;
    fetchBondStatus(period.year(), period.month() + 1, brandCode, true);
  };

  const content = useMemo(
    () =>
      bondStatus
        ? buildSapBondStatusContentHtml({
            brandName: bondStatus.brandName || bondStatus.brandCode,
            period: formatSapBondStatusPeriod(
              bondStatus.year,
              bondStatus.month,
            ),
            data: bondStatus.loading ? null : bondStatus.data,
            t,
          })
        : '',
    [bondStatus, t],
  );

  return (
    <FieldBlock>
      <FieldHeader>
        <InfoSectionTitle>{t('채권현황')}</InfoSectionTitle>
      </FieldHeader>
      <ControlRow>
        <Select
          value={brandCode}
          options={brandOptions}
          placeholder={t('브랜드')}
          onChange={(e) => setBrandCode(String(e.target.value))}
          sx={{ width: 180, height: CONTROL_HEIGHT }}
        />
        <DatePicker
          value={period}
          onChange={setPeriod}
          views={['year', 'month']}
          openTo="month"
          format="YYYY-MM"
          sx={{ width: 160 }}
        />
        <BoxButton
          type="button"
          variant="secondary"
          size="medium"
          disabled={isLoading || !brandCode}
          onClick={handleConfirm}
        >
          {t('확인')}
        </BoxButton>
      </ControlRow>
      {content && <SapBondStatusSnapshot content={content} hideTitle />}
      {isLoading && (
        <LoadingMessage>{t('데이터를 불러오는 중입니다...')}</LoadingMessage>
      )}
    </FieldBlock>
  );
};

const ControlRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
});

const LoadingMessage = styled(Body2)({
  marginTop: 16,
  textAlign: 'center',
  color: 'var(--dk-text-sub, #6B7280)',
});

export default SapBondStatusField;
